import React, { useEffect, useState, useRef } from 'react'
import { ProductFilterProps } from '@interfaces/filters'
import Icon from '@components/atoms/Icon'
import Image from '@components/atoms/Image'
import SwatchContainer from '@components/atoms/SwatchContainer'
import { cleanLabel } from '@helpers/global'
import MultiRangeSliderReact from 'multi-range-slider-react'
import LensTechDrawer from '../atoms/LensTechDrawer'

const ProductFilter: React.FC<ProductFilterProps> = ({
  filters,
  onFilterChange,
  checkboxStates,
  handleClearAll,
  searchQuery,
  setSearchQuery,
  swatchAppearance,
  productsToShow,
  totalProducts,
  lensTechIcon
}) => {
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isScrollingUp, setIsScrollingUp] = useState(false);
  const [prevMinValue, setPrevMinValue] = useState(0);
  const [prevMaxValue, setPrevMaxValue] = useState(300);
  const isFirstRender = useRef(true);
  const [isLensTechDrawerOpen, setIsLensTechDrawerOpen] = useState<boolean>(false);
  const handleCloseLensTechDrawer: () => void = () => setIsLensTechDrawerOpen(false)

  const priceFilter = filters.find(f => f.filterType === 'price_range');
  const parsedMin = typeof priceFilter?.priceMin === 'string'
    ? parseFloat(priceFilter.priceMin.replace(/[^0-9.]/g, ''))
    : priceFilter?.priceMin

  const parsedMax = typeof priceFilter?.priceMax === 'string'
    ? parseFloat(priceFilter.priceMax.replace(/[^0-9.]/g, ''))
    : priceFilter?.priceMax

  // Check if there’s an active price filter
  const priceFilterValues = Object.keys(checkboxStates).filter(key => {
    return (
      checkboxStates[key] &&
      /^\d+(\.\d+)?-\d+(\.\d+)?$/.test(key) // Only numeric ranges like "50-200"
    );
  });

  useEffect(() => {
    if (priceFilterValues.length > 0) {
      const [min, max] = priceFilterValues[0].split('-').map(Number);
      if (!isNaN(min) && !isNaN(max)) {
        setPrevMinValue(min)
        setPrevMaxValue(max)
      }
    } else {
      setPrevMinValue(parsedMin as number)
      setPrevMaxValue(parsedMax as number)
    }
  }, [checkboxStates, parsedMin, parsedMax]);

  const handlePriceRangeChange = (min: number, max: number) => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Only update if values have actually changed
    if (min !== prevMinValue || max !== prevMaxValue) {
      setPrevMinValue(min);
      setPrevMaxValue(max);

      if (priceFilter) {
        const label = `${min}-${max}`;
        onFilterChange(label, priceFilter.paramName);
      }
    }
  }

  //Check if user is scrolling up for sticky Close Filters Button
  useEffect(() => {
    let lastScroll = window.scrollY;

    function handleScroll() {
      const currentScroll = window.scrollY;
      setIsScrollingUp(currentScroll < lastScroll);
      lastScroll = currentScroll;
    }

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  //Remove Product Type filters from filters array if there is only one product type in a collection
  const updatedFilters = filters.filter(filter =>
    (filter.label !== 'Product Type' || filter.values.length > 1) &&
    filter.label !== 'Filters: On Sale?'
  );

  const handleCheckboxChange = (filter: string, paramName: string) => {
    onFilterChange(filter, paramName)
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value
    setSearchQuery(query)
  }

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen)
  }

  return (
    <div className={`lg:sticky lg:duration-500 lg:transition-all ${!isScrollingUp && 'lg:top-4'} ${isScrollingUp && 'lg:top-16'}`}>
      <LensTechDrawer
        isOpen={isLensTechDrawerOpen}
        onClose={handleCloseLensTechDrawer}
        lensTechIcon={lensTechIcon}
      />
      <button
        className={`flex items-center justify-center cursor-pointer w-full p-3 gap-2 border border-black bg-white text-black lg:hidden ${isFilterOpen && !isScrollingUp && 'sticky top-2 z-2'} ${isFilterOpen && isScrollingUp && 'sticky top-13 z-2'}`}
        type="button"
        onClick={toggleFilter}
      >
        <Icon className="w-4 h-2.5" name="filters" />
        <span>
          {!isFilterOpen && (
            window.DEPT.Strings.filters.filters
          )}
          {isFilterOpen && Object.values(checkboxStates).some(state => state === true) ? (
            window.DEPT.Strings.filters.applyCloseFilters
          ) : isFilterOpen && (
            window.DEPT.Strings.filters.closeFilters
          )}
        </span>
      </button>
      <div
        className={`transition-all duration-300 ease-in-out ${
          isFilterOpen ? 'opacity-100 h-auto' : 'opacity-0 h-0 overflow-hidden'
        } lg:opacity-100 lg:h-auto lg:overflow-visible`}
      >
        <div className="flex items-center justify-between">
          <p className="body-xl font-appleGaramond">{window.DEPT.Strings.filters.filters}</p>
          <button onClick={handleClearAll} className="body-sm">
            {window.DEPT.Strings.filters.clearAll}
          </button>
        </div>
        <div className="py-5 border-b border-grey-4 body-xs font-normal text-black">
          Showing {productsToShow} of {totalProducts} Products
        </div>
        <div className="divide-y divide-grey-4 border-b border-grey-4 lg:max-h-[calc(100vh-200px)] lg:overflow-y-auto lg:scrollbar-thin lg:scrollbar-thumb-gray-300 lg:scrollbar-track-gray-100 lg:p-1">
          <div className="py-5">
            <details className="group" open>
              <summary className="flex cursor-pointer list-none items-center justify-between font-medium">
                <span>{window.DEPT.Strings.filters.search}</span>
                <span className="transition group-open:rotate-180">
                  <Icon name="chevron-down" className="text-black w-4 h-4" />
                </span>
              </summary>
              <div className="mt-7">
                <div className="relative">
                  <Icon
                    name="search"
                    className="text-black w-4.5 h-4.5 absolute top-1/2 -translate-y-1/2 left-3.5 z-1"
                  />
                  <input
                    type="text"
                    placeholder="Keyword"
                    value={searchQuery}
                    onChange={handleSearchChange}
                    className="body-sm text-black bg-grey-5 pl-13 w-full pr-3 py-3 border border-grey-4 rounded-full shadow-search placeholder:body-sm placeholder:text-grey-6 focus:outline-none"
                  />
                </div>
              </div>
            </details>
          </div>
          {updatedFilters.map((filter) => (
            <div key={filter.label} className="py-5">
              <details className="group" open={filter.presentation === "image"}>
                <summary className="flex cursor-pointer list-none items-center justify-between font-medium">
                  <span>{filter.label} {filter.label === "Lens Tech" && (
                    <button
                      aria-label="Lens Tech Drawer Open"
                      className="w-4 h-4"
                      onClick={() => setIsLensTechDrawerOpen(true)}>
                      <Icon name="question"
                      aria-label="View Lens Tech Information Drawer"
                      aria-expanded={isLensTechDrawerOpen}
                      aria-controls="lens-tech-drawer"
                      aria-haspopup="dialog"/>
                    </button>
                  )}
                  </span>

                  <span className="transition group-open:rotate-180">
                    <Icon name="chevron-down" className="text-black w-4 h-4" />
                  </span>
                </summary>
                {(() => {
                  if (filter.filterType === 'price_range') {
                    const onSaleFilter = filters.find(f => f.label === 'Filters: On Sale?');
                    return (
                      <div className="flex flex-col gap-3 mt-7">
                        <MultiRangeSliderReact
                          min={parsedMin}
                          max={parsedMax}
                          minValue={prevMinValue}
                          maxValue={prevMaxValue}
                          ruler={false}
                          label={false}
                          className="bg-white !border-none !shadow-none !border-0"
                          step={1}
                          barInnerColor={'black'}
                          onChange={(e) => {
                            const minVal = Number(e.minValue)
                            const maxVal = Number(e.maxValue)
                            handlePriceRangeChange(minVal, maxVal)
                          }}
                        />
                        <div className="flex justify-between -mt-2">
                          <span className="body-xs text-gray-700">${prevMinValue}</span>
                          <span className="body-xs text-gray-700">${prevMaxValue}</span>
                        </div>
                        {onSaleFilter && (
                          <div className="mt-7 flex flex-col gap-4">
                            {onSaleFilter.values.map((value: any, index: number) => {
                              const inputId = `Filter-${onSaleFilter.paramName.trim()}-${index}`
                              return (
                                <div key={inputId} className="flex items-center space-x-2">
                                  <input
                                    type="checkbox"
                                    id={inputId}
                                    checked={checkboxStates[value.label] || false}
                                    onChange={() => handleCheckboxChange(value.label, onSaleFilter.paramName)}
                                    className="w-4.5 h-4.5 cursor-pointer rounded-sm border border-blue-1 bg-gradient-model-grey shadow-thumbnail"
                                  />
                                  <label htmlFor={inputId} className="ml-2 cursor-pointer body-xs font-medium text-black">
                                    {cleanLabel(value.label)} ({value.count})
                                  </label>
                                </div>
                              )
                            })}
                          </div>
                          )}
                      </div>
                    )
                  }
                  switch (filter.presentation) {
                    case 'swatch':
                      return (
                        <SwatchContainer
                          values={filter.values}
                          checkboxStates={checkboxStates}
                          onFilterChange={handleCheckboxChange}
                          swatchAppearance={swatchAppearance}
                          paramName={filter.paramName}
                        />
                      )
                    case 'image':
                      return (
                        <div className="mt-7 flex flex-wrap gap-2">
                          {filter.values.map((value: any, index: number) => {
                            const inputId = `Filter-${filter.paramName.trim()}-${index}`
                            const isSelected = checkboxStates[value.label] || false
                            return (
                              <div
                                key={inputId}
                                className={`max-w-16 rounded-sm border border-blue-1 bg-gradient-model-grey shadow-thumbnail aspect-[64/55] flex flex-col items-center justify-center ${
                                  isSelected ? 'outline outline-2 outline-offset-2 outline-blue-2' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  id={inputId}
                                  checked={checkboxStates[value.label] || false}
                                  onChange={() => handleCheckboxChange(value.label, filter.paramName)}
                                  className="hidden"
                                />
                                <label
                                  htmlFor={inputId}
                                  className="w-[95%] cursor-pointer body-xs font-medium text-black"
                                >
                                  {value.image && (
                                    <Image
                                      alt={value.image.alt || value.label}
                                      src={value.image.src}
                                      srcWidths={{ sm: 70 }}
                                      height={60}
                                      width={70}
                                      classes="aspect-[58/39] object-contain"
                                    />
                                  )}
                                </label>
                                <p className="body-xxxs font-medium text-black truncate w-[95%] text-center">{value.label}</p>
                              </div>
                            )
                          })}
                        </div>
                      )
                    default:
                      return (
                        <div className="mt-7 flex flex-col gap-4">
                          {filter.values.map((value: any, index: number) => {
                            const inputId = `Filter-${filter.paramName.trim()}-${index}`
                            return (
                              <div key={inputId} className="flex items-center space-x-2">
                                <input
                                  type="checkbox"
                                  id={inputId}
                                  checked={checkboxStates[value.label] || false}
                                  onChange={() => handleCheckboxChange(value.label, filter.paramName)}
                                  className="w-4.5 h-4.5 cursor-pointer rounded-sm border border-blue-1 bg-gradient-model-grey shadow-thumbnail"
                                />
                                <label htmlFor={inputId} className="ml-2 cursor-pointer body-xs font-medium text-black">
                                  {cleanLabel(value.label)} ({value.count})
                                </label>
                              </div>
                            )
                          })}
                        </div>
                      )
                  }
                })()}
              </details>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default ProductFilter
